/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { SafeUser, SurveyUserRoles } from '../../constants/users';
import { SurveyActionData } from '../../model/surveyData';
import { InitiativePermissions } from './InitiativePermissions';

type PermissionsUser = Pick<SafeUser, '_id' | 'permissions'>;
type PermissionsSurvey = Pick<SurveyActionData, '_id' | 'roles' | 'stakeholders' | 'initiativeId'>;

export class SurveyPermissions {
  static canContributeRoles = [SurveyUserRoles.Stakeholder];
  static canVerifyRoles = [SurveyUserRoles.Verifier];

  public static canManage(survey: Pick<PermissionsSurvey, 'roles' | 'initiativeId'>, user: PermissionsUser): boolean {
    const userId = String(user._id);
    if (survey.roles?.admin?.some(adminId => String(adminId) === userId)) {
      return true;
    }

    return InitiativePermissions.canManageInitiative(user, survey.initiativeId);
  }

  public static canAccess(survey: PermissionsSurvey, user: PermissionsUser): boolean {
    const userId = user._id;

    if (survey.stakeholders.stakeholder?.find((id) => id === userId)) {
      return true;
    }

    if (survey.stakeholders.verifier?.find((id) => id === userId)) {
      return true;
    }

    if (survey.roles?.admin?.some(adminId => String(adminId) === userId)) {
      return true;
    }

    if (survey.roles?.viewer?.some(adminId => String(adminId) === userId)) {
      return true;
    }

    // Check if user has initiative permission
    return InitiativePermissions.canAccess(user, survey.initiativeId);
  }

  public static canAccessAllData(survey: PermissionsSurvey, user: PermissionsUser): boolean {
    const userId = user._id;

    // Check if user has survey-level permissions
    if (survey.stakeholders.stakeholder?.find((id) => id.toString() === userId)) {
      return true;
    }
    if (survey.stakeholders.verifier?.find((id) => id.toString() === userId)) {
      return true;
    }
    if (survey.roles?.admin?.some(adminId => String(adminId) === userId)) {
      return true;
    }

    // Check if user has initiative permission
    if (InitiativePermissions.canAccessAllSurveyData(user, survey.initiativeId)) {
      return true;
    }

    return false;
  }

  public static canContribute(survey: PermissionsSurvey, user: PermissionsUser): boolean {
    const userId = user._id;

    // Check if user has survey permission
    if (survey.stakeholders.stakeholder?.find((id) => id === userId)) {
      return true;
    }

    if (survey.roles?.admin?.some(adminId => String(adminId) === userId)) {
      return true;
    }

    // Check if user has initiative permission
    return InitiativePermissions.canContribute(user, survey.initiativeId);
  }

  public static canVerify(survey: PermissionsSurvey, user: PermissionsUser): boolean {
    const userId = user._id;

    // Check if user has survey permission
    if (survey.stakeholders.verifier.find((id) => id === userId)) {
      return true;
    }

    if (survey.roles?.admin?.some(adminId => String(adminId) === userId)) {
      return true;
    }

    // Check if user has initiative permission
    return InitiativePermissions.canVerify(user, survey.initiativeId);
  }

  public static isStakeholder(survey: PermissionsSurvey, user: PermissionsUser): boolean {
    if (SurveyPermissions.canContribute(survey, user)) {
      return true;
    }

    if (SurveyPermissions.canVerify(survey, user)) {
      return true;
    }

    return SurveyPermissions.canManage(survey, user);
  }
}
